import { useState } from 'react';
import { Plus, UploadCloud, Library } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '@/components/ui';
import { ASSET_TYPE_ORDER, type AssetType, type MerchantAsset } from '@/types/merchant-asset';
import { useAuth } from '@/hooks/use-auth';
import { ROLE_GROUPS } from '@/types/auth';
import {
  useCreateMerchantAsset,
  useUpdateMerchantAsset,
  useDeleteMerchantAsset,
} from '@/hooks/use-merchant-assets';
import { useReplaceAsset } from './use-replace-asset';
import { replaceResultToast } from './replace-asset-toast';
import { uploadAssetFile } from '@/lib/api/merchant-assets';
import { AssetRow } from './asset-row';
import { AssetUpsertDialog, type AssetUpsertValues } from './asset-upsert-dialog';
import { PropagationDialog } from './propagation-dialog';
import { AssetDeleteDialog } from './asset-delete-dialog';

interface AssetLibraryProps {
  merchantId: string;
  assets: MerchantAsset[];
}

export function AssetLibrary({ merchantId, assets }: AssetLibraryProps) {
  const { t } = useTranslation('merchant-assets');
  // Issue #334: Write-Aktionen nur fuer Schreib-Rollen (alle ausser merchant_viewer).
  // Konsistent zum BFF-Gate; merchant_viewer sieht die Bibliothek read-only.
  const { hasRole } = useAuth();
  const canWrite = hasRole(ROLE_GROUPS.MERCHANT_ASSET_WRITE);
  const createMutation = useCreateMerchantAsset(merchantId);
  const updateMutation = useUpdateMerchantAsset(merchantId); // inline rename only
  const deleteMutation = useDeleteMerchantAsset(merchantId);
  const replaceMutation = useReplaceAsset(merchantId);

  const [createOpen, setCreateOpen] = useState(false);
  const [propagateAsset, setPropagateAsset] = useState<MerchantAsset | null>(null);
  const [deleteAsset, setDeleteAsset] = useState<MerchantAsset | null>(null);

  const openCreate = () => setCreateOpen(true);

  const handleUpload = (file: File, type: AssetType) =>
    uploadAssetFile({ merchantId, category: type, fileName: file.name, contentType: file.type || 'application/octet-stream' }, file);

  const handleCreate = async (values: AssetUpsertValues) => {
    try {
      await createMutation.mutateAsync({
        merchantId,
        name: values.name,
        type: values.type,
        currentFileUrl: values.currentFileUrl,
      });
      toast.success(t('upsert.createSuccess'));
      setCreateOpen(false);
    } catch {
      toast.error(t('actionError'));
    }
  };

  // Inline rename from the library row (Spec §4).
  const handleRename = async (asset: MerchantAsset, newName: string) => {
    try {
      await updateMutation.mutateAsync({ assetId: asset.assetId, input: { merchantId, name: newName } });
      toast.success(t('upsert.renameSuccess'));
    } catch {
      toast.error(t('actionError'));
    }
  };

  // Issue #417: "Replace file" no longer PUTs currentFileUrl (which retires the
  // asset to status=replaced). It creates a new asset, relinks every referencing
  // service, and soft-deletes the old one — orchestrated in useReplaceAsset. The
  // toast is derived from the structured result so it reports the real blast radius.
  const handlePropagate = async (newFileUrl: string) => {
    if (!propagateAsset) return;
    try {
      const result = await replaceMutation.mutateAsync({ asset: propagateAsset, newFileUrl });
      setPropagateAsset(null);
      const t0 = replaceResultToast(result);
      const message = t0.params ? t(t0.key, t0.params) : t(t0.key);
      const options = t0.descriptionKey ? { description: t(t0.descriptionKey) } : undefined;
      if (t0.level === 'warning') toast.warning(message, options);
      else toast.success(message, options);
    } catch {
      toast.error(t('actionError'));
    }
  };

  const handleDelete = async () => {
    if (!deleteAsset) return;
    try {
      await deleteMutation.mutateAsync(deleteAsset.assetId);
      toast.success(t('remove.success'));
      setDeleteAsset(null);
    } catch {
      toast.error(t('actionError'));
    }
  };

  const dialogs = (
    <>
      <AssetUpsertDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={handleCreate}
        onUpload={handleUpload}
        isPending={createMutation.isPending}
      />
      <PropagationDialog
        open={!!propagateAsset}
        onOpenChange={(open) => { if (!open) setPropagateAsset(null); }}
        asset={propagateAsset}
        onConfirm={handlePropagate}
        onUpload={handleUpload}
        isPending={replaceMutation.isPending}
      />
      <AssetDeleteDialog
        open={!!deleteAsset}
        onOpenChange={(open) => { if (!open) setDeleteAsset(null); }}
        asset={deleteAsset}
        onConfirm={handleDelete}
        isPending={deleteMutation.isPending}
      />
    </>
  );

  if (assets.length === 0) {
    return (
      <>
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-20 text-center">
          <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
            <UploadCloud className="h-6 w-6" />
          </div>
          <h3 className="text-base font-medium">{t('empty.title')}</h3>
          <p className="mt-1 max-w-sm text-sm text-muted-foreground">{t('empty.body')}</p>
          {canWrite && (
            <Button className="mt-5" onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" />
              {t('library.firstAsset')}
            </Button>
          )}
        </div>
        {canWrite && dialogs}
      </>
    );
  }

  const grouped = ASSET_TYPE_ORDER.map((type) => ({
    type,
    items: assets.filter((a) => a.type === type),
  })).filter((g) => g.items.length > 0);

  // „ungenutzt" nur, wenn die Nutzung auch ermittelbar war — sonst widerspräche die
  // Summary den Zeilen, die „Nutzung unbekannt" zeigen (Spec §3 Degradation).
  const unused = assets.filter((a) => !a.usageUnavailable && a.usedBy.length === 0).length;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">
          {t('library.summary', { count: assets.length })}
          {unused > 0 && (
            <span className="text-muted-foreground/70"> · {t('library.unusedSuffix', { count: unused })}</span>
          )}
        </p>
        {canWrite && (
          <Button size="sm" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            {t('library.newAsset')}
          </Button>
        )}
      </div>

      {grouped.map(({ type, items }) => (
        <section key={type}>
          <div className="mb-2 flex items-baseline gap-2 px-1">
            <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t(`types.${type as AssetType}`)}
            </h2>
            <span className="text-xs text-muted-foreground/60">{items.length}</span>
          </div>
          <div className="divide-y rounded-lg border bg-card">
            {items.map((asset) => (
              <AssetRow
                key={asset.assetId}
                asset={asset}
                canWrite={canWrite}
                onRename={handleRename}
                onReplaceFile={setPropagateAsset}
                onDelete={setDeleteAsset}
              />
            ))}
          </div>
        </section>
      ))}

      <p className="flex items-center gap-1.5 px-1 text-xs text-muted-foreground/70">
        <Library className="h-3.5 w-3.5" />
        {t('library.merchantWideNote')}
      </p>

      {canWrite && dialogs}
    </div>
  );
}
